import { useEffect } from 'react';

export const BEFOREUNLOAD = 'beforeunload';

type UnloadEvent = Event & {
	returnValue: unknown;
};

/**
 * This component is used to prompt the user when they try to navigate away from a
 * page.
 * Typically, it is used to prompt when they have unsaved changes in a form, or
 * some other state that they might lose of it is not saved.
 *
 * @param beforeUnload - If true, prompt will be enabled, and the event listener is
 * added to the window. If false, the event listener is removed.
 *
 * @param onShouldShowDirtyFormPrompt - A closure to be evaluated, that returns true if the user
 * should be prompted, and false if they should not be prompted. If this is not
 * provided, the user will always be prompted. Not used directly by consumers, but instead
 */
export const UnloadPrompt = ({
	beforeUnload,
	onShouldShowDirtyFormPrompt,
}: {
	beforeUnload: boolean;
	// This is a pretty interesting type, when we cleanup jfp.magma.remove-unsafe-unload-prompt the code we should remove the null case and just stick
	// with optional param
	onShouldShowDirtyFormPrompt?: null | (() => boolean);
}) => {
	useEffect(() => {
		const onBeforeUnload = (e: UnloadEvent) => {
			if (typeof onShouldShowDirtyFormPrompt === 'function' && !onShouldShowDirtyFormPrompt()) {
				return undefined;
			}

			e.preventDefault(); // Firefox
			const message = '';
			// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
			e.returnValue = message; // Chrome
			return message; // IE
		};
		if (beforeUnload) {
			window.addEventListener(BEFOREUNLOAD, onBeforeUnload);
		} else {
			window.removeEventListener(BEFOREUNLOAD, onBeforeUnload);
		}
		return () => {
			window.removeEventListener(BEFOREUNLOAD, onBeforeUnload);
		};
	}, [beforeUnload, onShouldShowDirtyFormPrompt]);

	return null;
};
